import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useMemo } from 'react';
import clamp from 'lodash/clamp';
import { Col, Row, Spinner, Table } from '@cancercentrum/rcc-bootstrap';
import keyBy from 'lodash/keyBy';
import { useUncontrolledProp } from 'uncontrollable';
import without from 'lodash/without';
import isNil from 'lodash/isNil';
import classNames from 'classnames';
import { useI18n } from '../../i18n';
import { GridHeaders } from './GridHeaders';
import { GridBody } from './GridBody';
import { PageSizeSelector } from './PageSizeSelector';
import { Pager } from './Pager';
import { RowFilter } from './RowFilter';
import { FilterStatus } from './FilterStatus';
import * as gridUtils from './utils';
import { toggleSort } from './utils';
import { SimpleColumnFilter, defaultSimpleColumnFilter } from './SimpleColumnFilter';
function useSortedAndFilteredRows(rows, columns, filter, columnFilters, sort, manual) {
    return useMemo(()=>{
        if (manual) {
            return rows;
        }
        const filters = [];
        let result = rows;
        Object.keys(columnFilters).forEach((columnId)=>{
            const f = columnFilters[columnId];
            const column = columns.find((x)=>x.id === columnId);
            const columnFilter = Array.isArray(column?.filters) ? column?.filters?.find((x)=>x.type === f.type) : column?.filters;
            if (columnFilter && column) {
                filters.push({
                    fn: columnFilter.filter,
                    column: column,
                    value: f.value
                });
            }
        });
        result = gridUtils.filter(result, filter, columns);
        result = gridUtils.applyColumnFilters(result, filters);
        const columnsById = keyBy(columns, (x)=>x.id);
        const sortCols = sort.reduce((reduction, x)=>{
            const col = columnsById[x.column];
            if (col) {
                reduction.push({
                    column: col,
                    direction: x.direction
                });
            } else if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn(`Grid: Skipping sort field "${x.column}. No column with that id or field found. Available names: ${Object.keys(columnsById).join(', ')}`);
            }
            return reduction;
        }, []);
        result = gridUtils.sort(result, sortCols);
        return result;
    }, [
        rows,
        columns,
        filter,
        columnFilters,
        sort,
        manual
    ]);
}
const DefaultPageSizes = [
    10,
    15,
    25,
    50
];
function Grid(props) {
    const { autoFocusSearch = false, availablePageSizes = DefaultPageSizes, columns: columnsProp, defaultPageSize = 15, onRowClick, rows, searchable = true, filterable = true, sortable = true, selectable = false, getId, responsive = true, hover = true, condensed = true, renderRow, renderBody, multiSort = true, multiSelect = false, paginationPosition = [
        'bottom'
    ], pageSizeSelectorPosition = [
        'top'
    ], loading, manual = false, hideResetFilterButton, showFilterStatus = true } = props;
    const [value, onValueChange] = useUncontrolledProp(props.value, multiSelect ? [] : null, props.onValueChange);
    const [filter, onFilterChange] = useUncontrolledProp(props.filter, '', props.onFilterChange);
    const [activePage, onActivePageChange] = useUncontrolledProp(props.activePage, 1, props.onActivePageChange);
    const [pageSize, onPageSizeChange] = useUncontrolledProp(props.pageSize, defaultPageSize, props.onPageSizeChange);
    const [sort, onSortChange] = useUncontrolledProp(props.sort, [], props.onSortChange);
    const [columnFilters, onColumnFiltersChange] = useUncontrolledProp(props.columnFilters, {}, props.onColumnFiltersChange);
    const { t } = useI18n();
    // Default kolumnfilter
    const defaultSimpleFilter = useMemo(()=>({
            type: 'SIMPLE_COLUMN_FILTER',
            Filter: SimpleColumnFilter,
            filter: defaultSimpleColumnFilter
        }), []);
    // Lägg till defaultfilter på alla kolumner
    const columns = useMemo(()=>{
        return columnsProp.map((x)=>{
            const filters = Array.isArray(x.filters) ? x.filters : x.filters ? Object.assign({}, defaultSimpleFilter, x.filters) : defaultSimpleFilter;
            return {
                ...x,
                filters: filters
            };
        });
    }, [
        columnsProp,
        defaultSimpleFilter
    ]);
    const sortedAndFilteredRows = useSortedAndFilteredRows(rows, columns, filter, columnFilters, sort, manual);
    const onRowClickCb = (item, e, source)=>{
        const id = getId(item);
        if (selectable) {
            let newVal;
            if (Array.isArray(value)) {
                newVal = value.includes(id) ? without(value, id) : [
                    ...value,
                    id
                ];
            } else {
                newVal = id;
            }
            onValueChange(newVal);
        }
        onRowClick && onRowClick(item, e, source);
    };
    const clearSort = ()=>{
        onSortChange([]);
    };
    const sortChange = (columnId)=>{
        let newSort = toggleSort(sort, columnId);
        if (!multiSort) {
            const m = newSort.find((x)=>x.column === columnId);
            newSort = m ? [
                m
            ] : [];
        }
        onSortChange(newSort);
    };
    const usesPagination = props.totalRowCount !== undefined;
    const totalItemCount = usesPagination ? props.totalRowCount : rows.length;
    const sortedAndFilteredLength = usesPagination ? totalItemCount : sortedAndFilteredRows.length;
    const numberOfPages = Math.ceil(sortedAndFilteredLength / pageSize);
    const curActivePage = clamp(activePage, 1, numberOfPages);
    const startIndex = (curActivePage - 1) * pageSize;
    const currentPageRows = usesPagination ? sortedAndFilteredRows : sortedAndFilteredRows.slice(startIndex, startIndex + pageSize);
    const onRowClickHandler = selectable || onRowClick ? onRowClickCb : undefined;
    const loadingText = t('component.grid.loading');
    return /*#__PURE__*/ _jsxs("div", {
        className: classNames('rcc-grid', {
            'loading': loading
        }),
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                className: "mb-3",
                children: [
                    searchable && /*#__PURE__*/ _jsx(RowFilter, {
                        focusFilter: autoFocusSearch,
                        filter: filter,
                        onChange: onFilterChange,
                        clearSort: clearSort,
                        hideResetFilterButton: hideResetFilterButton
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        className: "d-flex justify-content-end",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "d-flex flex-column",
                            children: [
                                pageSizeSelectorPosition.includes('top') && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        availablePageSizes.length > 1 && /*#__PURE__*/ _jsx(PageSizeSelector, {
                                            value: pageSize,
                                            onChange: (e)=>{
                                                onPageSizeChange(parseInt(e.target.value, 10));
                                            },
                                            availableSizes: availablePageSizes
                                        }),
                                        showFilterStatus && /*#__PURE__*/ _jsx(FilterStatus, {
                                            activePage: curActivePage,
                                            pageSize: pageSize,
                                            filteredItems: sortedAndFilteredLength
                                        })
                                    ]
                                }),
                                totalItemCount > pageSize && paginationPosition.includes('top') && /*#__PURE__*/ _jsx(Pager, {
                                    items: numberOfPages,
                                    activePage: curActivePage,
                                    onSelect: onActivePageChange
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Table, {
                responsive: responsive,
                hover: hover,
                condensed: condensed,
                children: [
                    /*#__PURE__*/ _jsx(GridHeaders, {
                        columns: columns,
                        columnFilters: columnFilters,
                        sort: sort,
                        filterable: filterable,
                        sortable: sortable,
                        onSortChange: sortChange,
                        onColumnFilterChange: (field, selectedFilter, filterValue)=>{
                            const res = {
                                ...columnFilters
                            };
                            if (!filterValue) {
                                delete res[field];
                            } else {
                                res[field] = {
                                    type: selectedFilter.type,
                                    value: filterValue
                                };
                            }
                            onColumnFiltersChange(res);
                        }
                    }),
                    /*#__PURE__*/ _jsx(GridBody, {
                        items: currentPageRows,
                        getKey: getId,
                        columns: columns,
                        filter: filter,
                        selectable: selectable,
                        selectedIds: Array.isArray(value) ? value : isNil(value) ? [] : [
                            value
                        ],
                        onRowClick: onRowClickHandler,
                        renderRow: renderRow,
                        renderBody: renderBody,
                        sort: sort
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Col, {
                className: "d-flex justify-content-end",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "d-flex flex-column",
                    children: [
                        pageSizeSelectorPosition.includes('bottom') && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                availablePageSizes.length > 1 && /*#__PURE__*/ _jsx(PageSizeSelector, {
                                    value: pageSize,
                                    onChange: (e)=>{
                                        onPageSizeChange(parseInt(e.target.value, 10));
                                    },
                                    availableSizes: availablePageSizes
                                }),
                                /*#__PURE__*/ _jsx(FilterStatus, {
                                    activePage: curActivePage,
                                    pageSize: pageSize,
                                    filteredItems: sortedAndFilteredLength
                                })
                            ]
                        }),
                        totalItemCount > pageSize && paginationPosition.includes('bottom') && /*#__PURE__*/ _jsx(Pager, {
                            items: numberOfPages,
                            activePage: curActivePage,
                            onSelect: onActivePageChange
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "loading-overlay",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "loading-text",
                    children: [
                        /*#__PURE__*/ _jsx(Spinner, {
                            className: "me-2",
                            bsSize: "sm",
                            bsStyle: "primary"
                        }),
                        " ",
                        loadingText
                    ]
                })
            })
        ]
    });
}
export const unsafe_Grid = /*#__PURE__*/ memo(Grid);
